/**
 * What a caller may do to a file, as pure functions: the right-click menu, drop targets while
 * dragging, the Move to… list and Rename. They mirror the server's checks (spec 2026-10-14 §3–5)
 * so the UI only offers what will succeed; the API enforces them again.
 */
import { DOCUMENT_TYPE_BY_CODE, SITE_DOCUMENT_TYPES } from '@erp-attach/shared';
import type { DocumentSummary, FolderNode } from '../api/types';
import type { IconName } from '../components/ui/Icon';

export type MenuCommand = 'open' | 'preview' | 'download' | 'copyLink' | 'rename' | 'move' | 'details' | 'activity' | 'hide' | 'unhide' | 'hold' | 'remove';

export interface MenuEntry {
  command: MenuCommand;
  label: string;
  icon: IconName;
  danger?: boolean;
}

export type MenuGroup = MenuEntry[];

export interface MenuContext {
  meOid: string | null;
  /** The file's folder is archived (read-only). */
  folderArchived?: boolean;
  canAudit: boolean;
  moveTargets: readonly FolderNode[];
}

export type DropVerdict = { ok: true } | { ok: false; reason: string };

/** Types a folder files directly: its own list, or the site types for a per-site folder. */
export function folderAccepts(folder: FolderNode, typeCode: string): boolean {
  const types: readonly string[] = folder.documentTypes.length > 0 ? folder.documentTypes : folder.siteId ? SITE_DOCUMENT_TYPES : [];
  return types.includes(typeCode);
}

const isContainer = (f: FolderNode) => f.documentTypes.length === 0 && !f.siteId;

/** A folder anywhere in a tree, by id. */
export function findFolderIn(tree: readonly FolderNode[], id: string): FolderNode | null {
  for (const f of tree) {
    if (f.id === id) return f;
    const hit = findFolderIn(f.children, id);
    if (hit) return hit;
  }
  return null;
}

/**
 * `source` is the folder the document sits in; moves never leave its record (same entity type and
 * id — each job's site folder is its own record).
 */
export function dropVerdict(folder: FolderNode, doc: DocumentSummary, source: FolderNode | null): DropVerdict {
  if (doc.legalHold) return { ok: false, reason: 'Under legal hold' };
  if (!doc.actions.includes('hide')) return { ok: false, reason: "You can't move this file" };
  if (folder.id === doc.folderId) return { ok: false, reason: 'Already in this folder' };
  if (!source || source.entityType !== folder.entityType || source.entityId !== folder.entityId) return { ok: false, reason: 'Another record' };
  if (isContainer(folder)) return { ok: false, reason: 'Holds folders, not files' };
  if (folder.status === 'archived') return { ok: false, reason: 'Archived, read-only' };
  if (!folderAccepts(folder, doc.documentTypeCode)) {
    return { ok: false, reason: `Doesn't take ${DOCUMENT_TYPE_BY_CODE.get(doc.documentTypeCode)?.label ?? doc.documentTypeCode}` };
  }
  if (!folder.actions.includes('upload')) return { ok: false, reason: 'No upload rights' };
  return { ok: true };
}

export const canDrop = (folder: FolderNode, doc: DocumentSummary, source: FolderNode | null): boolean => dropVerdict(folder, doc, source).ok;

/** Every folder in the record's tree (any depth) the document could move to. */
export function moveTargets(tree: readonly FolderNode[], doc: DocumentSummary): FolderNode[] {
  const source = findFolderIn(tree, doc.folderId);
  const out: FolderNode[] = [];
  const walk = (list: readonly FolderNode[]) => {
    for (const f of list) {
      if (canDrop(f, doc, source)) out.push(f);
      walk(f.children);
    }
  };
  walk(tree);
  return out;
}

/**
 * The uploader may rename without hide rights, but not in an archived (read-only) folder; there
 * `hide` is already absent from `doc.actions` for everyone but Records.Admin.
 */
export function canRename(doc: DocumentSummary, meOid: string | null, folder: { archived: boolean } = { archived: false }): boolean {
  if (doc.legalHold || (doc.status !== 'available' && doc.status !== 'hidden')) return false;
  if (doc.actions.includes('hide')) return true;
  const uploader = !!meOid && !!doc.createdByOid && doc.createdByOid.toLowerCase() === meOid.toLowerCase();
  return uploader && !folder.archived;
}

const entry = (command: MenuCommand, label: string, icon: IconName, danger = false): MenuEntry => (danger ? { command, label, icon, danger } : { command, label, icon });

/** Menu groups in display order; an item the caller cannot use is left out, empty groups dropped. */
export function documentMenu(doc: DocumentSummary, ctx: MenuContext): MenuGroup[] {
  const can = (a: DocumentSummary['actions'][number]) => doc.actions.includes(a);
  const groups: MenuGroup[] = [
    [entry('open', 'Open', 'maximize'), entry('preview', 'Preview', 'panel-right'), ...(can('download') ? [entry('download', 'Download', 'download')] : []), entry('copyLink', 'Copy link', 'link')],
    [
      ...(canRename(doc, ctx.meOid, { archived: !!ctx.folderArchived }) ?[entry('rename', 'Rename…', 'pencil')] : []),
      ...(!doc.legalHold && can('hide') && ctx.moveTargets.length > 0 ? [entry('move', 'Move to…', 'move')] : []),
    ],
    [entry('details', 'Details', 'info'), ...(ctx.canAudit ? [entry('activity', 'View activity', 'history')] : [])],
    [
      ...(!doc.hidden && can('hide') ? [entry('hide', 'Hide', 'eye-off')] : []),
      ...(doc.hidden && can('unhide') ? [entry('unhide', 'Unhide', 'eye')] : []),
      ...(can('hold') ? [entry('hold', doc.legalHold ? 'Release hold' : 'Legal hold', 'shield')] : []),
      ...(can('remove') && doc.status !== 'purged' && !doc.legalHold ? [entry('remove', 'Remove…', 'x', true)] : []),
    ],
  ];
  return groups.filter((g) => g.length > 0);
}
